import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, errorMessage } from "../api";
import { useSession } from "../session";
import { Callout, Loading } from "../ui/bits";
import { Lockup } from "../ui/brand";
import { createPasskey, passkeysSupported, PasskeyError } from "../webauthn";

/** The invite link is <console>/accept-invite#<token>: the token stays in the fragment, so it
 *  never reaches a server log or a Referer header. */
function tokenFromHash(): string {
  return decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
}

export function AcceptInvite() {
  const { token: session, signIn } = useSession();
  const navigate = useNavigate();
  const [token] = useState(tokenFromHash);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optional: a passkey on this device lets them skip the emailed code next time.
  const [withPasskey, setWithPasskey] = useState(false);
  const [label, setLabel] = useState("");

  const info = useQuery({ queryKey: ["invite", token], queryFn: () => api.inviteInfo(token), enabled: token.length > 0, retry: false });

  // Take the token out of the address bar and history once it is in memory.
  useEffect(() => {
    if (token) window.history.replaceState(null, "", window.location.pathname);
  }, [token]);

  if (session) return <Navigate to="/" replace />;

  const claim = async () => {
    setBusy(true);
    setError(null);
    try {
      let passkey: { ceremony: string; credential: unknown; name?: string } | undefined;
      if (withPasskey) {
        const { ceremony, options } = await api.inviteBegin(token);
        passkey = { ceremony, credential: await createPasskey(options), name: label.trim() || undefined };
      }
      const r = await api.inviteAccept(token, passkey);
      signIn(r.token);
      navigate("/", { replace: true });
    } catch (err) {
      if (!(err instanceof PasskeyError && err.cancelled)) setError(errorMessage(err));
      else setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <main className="signin-col">
        <div className="signin-plate">
          <Lockup label="staff" />
        </div>
        <div className="card">
          <div className="card-body">
            <h1>Claim your account</h1>
            {!token ? (
              <Callout kind="danger">This page needs the link from your invite email. Open that link again.</Callout>
            ) : info.isLoading ? (
              <Loading />
            ) : info.isError ? (
              <Callout kind="danger">{errorMessage(info.error)}</Callout>
            ) : info.data ? (
              <>
                <div className="muted">
                  You were invited as <strong>{info.data.roles.join(", ") || "staff"}</strong> with <strong>{info.data.email}</strong>. Claim the account to get in.
                  Next time you sign in with a code we email you: there is no password.
                </div>
                {passkeysSupported() && (
                  <>
                    <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input type="checkbox" checked={withPasskey} onChange={(e) => setWithPasskey(e.target.checked)} data-testid="invite-with-passkey" />
                      <span>Also create a passkey on this device (optional)</span>
                    </label>
                    {withPasskey && (
                      <label className="field">
                        <span className="label">Name this passkey (optional)</span>
                        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Work laptop" maxLength={60} data-testid="invite-label" />
                        <span className="hint">Your fingerprint, face or device PIN then signs you in without a code. You can add or remove passkeys later under Security.</span>
                      </label>
                    )}
                  </>
                )}
                {error && (
                  <div role="alert">
                    <Callout kind="danger">{error}</Callout>
                  </div>
                )}
                <button className="primary big" onClick={() => void claim()} disabled={busy} data-testid="invite-claim">
                  {busy && <span className="spinner" />}
                  Claim account
                </button>
              </>
            ) : null}
          </div>
        </div>
        <p className="signin-foot">The operator console: players, payments, live-ops and safety for every SoftLife market.</p>
      </main>
    </div>
  );
}
